import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";
import {
  FacebookFilled,
  InstagramFilled,
  TwitterOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";

interface SocialLink {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  whatsapp?: string;
}

interface SocialLinksProps {
  socialLinks: SocialLink;
  variant?: "header" | "footer" | "floating";
  primaryColor?: string;
  size?: "small" | "medium" | "large";
  showLabels?: boolean;
  className?: string;
}

const SocialLinks: React.FC<SocialLinksProps> = ({
  socialLinks,
  variant = "footer",
  primaryColor = "#1890ff",
  size = "medium",
  showLabels = false,
  className = "",
}) => {
  const [isTouchViewport, setIsTouchViewport] = useState(false);

  useEffect(() => {
    const syncViewport = () => {
      setIsTouchViewport(window.innerWidth < 768);
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  const hasLinks = Object.values(socialLinks || {}).some(
    (link) => link && link.trim()
  );

  if (!hasLinks) {
    return null;
  }

  const socialConfig = [
    {
      key: "facebook",
      icon: FacebookFilled,
      label: "Facebook",
      color: "#1877F2",
      url: socialLinks.facebook,
    },
    {
      key: "instagram",
      icon: InstagramFilled,
      label: "Instagram",
      color: "#E4405F",
      url: socialLinks.instagram,
    },
    {
      key: "twitter",
      icon: TwitterOutlined,
      label: "Twitter",
      color: "#1DA1F2",
      url: socialLinks.twitter,
    },
    {
      key: "whatsapp",
      icon: WhatsAppOutlined,
      label: "WhatsApp",
      color: "#25D366",
      url: socialLinks.whatsapp,
    },
  ];

  const getVariantStyles = () => {
    switch (variant) {
      case "header":
        return {
          container: {
            display: "flex",
            alignItems: "center",
            gap: "12px",
          },
          iconSize: size === "small" ? 18 : size === "large" ? 28 : 22,
          showBackground: false,
        };

      case "floating":
        return {
          container: {
            display: "flex",
            flexDirection: "column" as const,
            gap: "16px",
            position: "fixed" as const,
            right: "20px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1000,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "25px",
            padding: "16px 8px",
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
          },
          iconSize: 24,
          showBackground: true,
        };

      default:
        return {
          container: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
            marginTop: showLabels ? "16px" : "12px",
          },
          iconSize: size === "small" ? 20 : size === "large" ? 32 : 24,
          showBackground: true,
        };
    }
  };

  const variantStyles = getVariantStyles();

  const formatUrl = (url: string, platform: string): string => {
    if (!url) return "";

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    switch (platform) {
      case "facebook":
        return url.startsWith("facebook.com")
          ? `https://${url}`
          : `https://facebook.com/${url.replace("@", "")}`;

      case "instagram":
        return url.startsWith("instagram.com")
          ? `https://${url}`
          : `https://instagram.com/${url.replace("@", "")}`;

      case "twitter":
        return url.startsWith("twitter.com")
          ? `https://${url}`
          : `https://twitter.com/${url.replace("@", "")}`;

      case "whatsapp": {
        const cleanNumber = url.replace(/[^\d+]/g, "");
        if (cleanNumber.match(/^\+?[\d\s\-()]+$/)) {
          return `https://wa.me/${cleanNumber.replace(/[^\d]/g, "")}`;
        }
        return url;
      }

      default:
        return url;
    }
  };

  return (
    <div
      className={`social-links ${className}`}
      style={variantStyles.container}
      data-variant={variant}
      data-touch={isTouchViewport ? "true" : "false"}
    >
      {socialConfig
        .filter((social) => social.url && social.url.trim())
        .map((social) => {
          const IconComponent = social.icon;
          const formattedUrl = formatUrl(social.url!, social.key);

          return (
            <Tooltip
              key={social.key}
              title={
                showLabels || isTouchViewport
                  ? null
                  : `Suivez-nous sur ${social.label}`
              }
              placement={variant === "floating" ? "left" : "top"}
            >
              <div
                className="social-icon"
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: variantStyles.showBackground
                    ? variantStyles.iconSize + 16
                    : "auto",
                  height: variantStyles.showBackground
                    ? variantStyles.iconSize + 16
                    : "auto",
                  borderRadius: variantStyles.showBackground ? "50%" : "4px",
                  background: variantStyles.showBackground
                    ? `linear-gradient(135deg, ${social.color}15, ${social.color}25)`
                    : "transparent",
                  border: variantStyles.showBackground
                    ? `2px solid ${social.color}30`
                    : "none",
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: "hidden",
                  textDecoration: "none",
                }}
                onClick={() =>
                  window.open(formattedUrl, "_blank", "noopener,noreferrer")
                }
                onMouseEnter={(e) => {
                  if (isTouchViewport) return;
                  const target = e.currentTarget;
                  target.style.transform =
                    variant === "floating"
                      ? "scale(1.1)"
                      : "translateY(-2px) scale(1.05)";
                  target.style.background = variantStyles.showBackground
                    ? `linear-gradient(135deg, ${social.color}25, ${social.color}35)`
                    : `${social.color}15`;
                  target.style.borderColor = `${social.color}60`;
                  target.style.boxShadow = `0 8px 25px ${social.color}25`;
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget;
                  target.style.transform = "translateY(0) scale(1)";
                  target.style.background = variantStyles.showBackground
                    ? `linear-gradient(135deg, ${social.color}15, ${social.color}25)`
                    : "transparent";
                  target.style.borderColor = `${social.color}30`;
                  target.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-100%",
                    left: "-100%",
                    width: "300%",
                    height: "300%",
                    background: `linear-gradient(45deg, transparent, ${social.color}20, transparent)`,
                    transform: "rotate(45deg)",
                    transition: "all 0.6s",
                    opacity: 0,
                  }}
                  className="shine-effect"
                />

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <IconComponent
                    style={{
                      fontSize: variantStyles.iconSize,
                      color: social.color,
                      transition: "color 0.3s ease",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />

                  {showLabels && (
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: social.color,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {social.label}
                    </span>
                  )}
                </div>
              </div>
            </Tooltip>
          );
        })}

      <style>{`
        .social-links:hover .shine-effect {
          opacity: 1 !important;
          top: -50% !important;
          left: -50% !important;
        }

        @media (max-width: 768px) {
          .social-links {
            gap: 12px !important;
            flex-wrap: wrap;
            justify-content: center;
          }

          .social-links[data-variant="header"] {
            gap: 8px !important;
          }

          .social-links[data-variant="floating"] {
            right: 10px !important;
            padding: 12px 6px !important;
          }
        }

        @media (max-width: 480px) {
          .social-links[data-variant="footer"] .social-icon {
            width: 38px !important;
            height: 38px !important;
          }

          .social-links[data-variant="footer"] .social-icon svg {
            font-size: 18px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SocialLinks;
